import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import BaseCard from "../../../components/ui/BaseCard";
import BaseTextField from "../../../components/ui/BaseTextField";
import { useDispatch, useSelector } from "react-redux";

import BaseCheckbox from "../../../components/ui/BaseCheckbox";
import {
  updateWorkOrderForm,
  updateWorkOrderFormValidation,
} from "../../../store/workOrder/workOrderSlice";
import BaseAutoSelect from "../../../components/ui/BaseAutoSelect";
import { debounce } from "../../../utils/helpers";
import { fetchAllClients } from "../../../services/api/client";
import { useCurrentUser } from "../../../hooks/useCurrentUser";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { fetchAllProjects } from "../../../services/api/project";
import useAuth from "../../../hooks/useAuth";

import { UserType, WorkOrderStatusType } from "../../../utils/enum";
const TITLE_MAX_LENGTH = 100;

const OverviewForm = forwardRef((props, ref) => {
  const dispatch = useDispatch();
  const { auth, authRole } = useAuth();
  // Customers only provide Title + Incident ID here; client/project/teams/options
  // are internal and hidden for them (client is server-derived on submit).
  const isCustomer = authRole === UserType.CUSTOMER;
  const companyId = auth?.user?.company?.id;
  const [clientSearchInput, setClientSearchInput] = useState("");
  const [projectSearchInput, setProjectSearchInput] = useState("");
  const [clientQueryParams, setClientQueryParams] = useState("");
  const [projectQueryParams, setProjectQueryParams] = useState("");
  const overview = useSelector((state) => {
    return state.workOrder.summary.overview;
  });
  const workOrderFormValidation = useSelector((state) => state.workOrder.workOrderFormValidation);
  // The client a customer raised their ticket against is theirs to choose — a
  // client reviewing it can see the field but not change it.
  const createdByRole = useSelector((state) => state.workOrder.summary.createdByRole);
  // The edit modals render this form with the work order as a prop and skip the
  // stepper's hydration, so read it straight off the prop when there is one.
  // Only a customer submission reaches PENDING_APPROVAL, so that stands in until
  // the API returns the creator's role.
  const raisedByRole = props.workOrderData
    ? (props.workOrderData?.createdBy?.role?.role ??
      (props.workOrderData?.status === WorkOrderStatusType.PENDING_APPROVAL
        ? UserType.CUSTOMER
        : null))
    : createdByRole;
  const isClientLocked = !isCustomer && raisedByRole === UserType.CUSTOMER;
  const { userData } = useCurrentUser();

  // The logged-in (parent) customer only selects among their own child clients.
  const customerParentId = auth?.user?.customer?.id ?? userData?.customerId;
  const { isPending: isClientsLoading, data: clientsData } = useQuery({
    queryKey: ["clients", clientQueryParams, companyId, isCustomer ? customerParentId : null],
    queryFn: () =>
      fetchAllClients({
        search: clientQueryParams,
        companyId: companyId?.toString(),
        isParentClient: false,
        ...(isCustomer && customerParentId ? { parentClientId: customerParentId } : {}),
      }),
    enabled: !!companyId,
    placeholderData: keepPreviousData,
  });

  // With no client chosen this lists every project, so the user can pick a
  // project first and have its client back-filled. Once a client is selected
  // the scoping happens server-side, so a match can't fall outside the page.
  const { isPending: isProjectsLoading, data: projectsData } = useQuery({
    queryKey: ["projects", projectQueryParams, companyId, overview?.clientId],
    queryFn: () =>
      fetchAllProjects({
        search: projectQueryParams,
        companyId: companyId?.toString(),
        clientId: overview?.clientId?.toString(),
      }),
    enabled: !!companyId,
    // Picking a project back-fills the client, which changes this query key.
    // Without keeping the previous page the option list blanks mid-refetch and
    // MUI loses the label for the value the user just chose.
    placeholderData: keepPreviousData,
  });

  useImperativeHandle(ref, () => ({
    // Returns { valid, errors } rather than firing a toast per bad field — the
    // stepper collects errors across every form and shows one summary, then
    // scrolls to the first offender. See utils/formErrors.js.
    isValid: () => {
      const errors = [];

      const fail = (fieldName, message) => {
        errors.push(message);
        dispatch(updateWorkOrderFormValidation({ fieldName, data: true }));
      };

      if (!overview?.title?.trim()) {
        fail("title", "Work Order Title is required");
      }

      // Customers now select their own client (required); Project stays hidden for them.
      if (!overview?.clientId) {
        fail("clientId", "Client is required");
      }

      // Project is hidden from customers — don't gate them on a field they can't see.
      if (!isCustomer && !overview?.projectId) {
        fail("projectId", "Project is required");
      }

      // Both teams are mandatory for everyone, but they are read-only and inherited
      // from the selected client, so they can only be judged once one is chosen —
      // until then the Client error already covers it. Clear the flags in that case
      // so an earlier Publish attempt can't leave them stranded in red.
      if (!overview?.clientId) {
        dispatch(updateWorkOrderFormValidation({ fieldName: "teamId", data: false }));
        dispatch(updateWorkOrderFormValidation({ fieldName: "secondaryTeamId", data: false }));
      } else {
        // An empty value means the chosen client has no team configured. Customers
        // can't see these fields at all, so point at the entity rather than at a
        // field they have no way to fill in.
        if (!overview?.teamId) {
          fail(
            "teamId",
            isCustomer
              ? "The selected entity has no Service Delivery Team assigned"
              : "Service Delivery Team is required",
          );
        }

        if (!overview?.secondaryTeamId) {
          fail(
            "secondaryTeamId",
            isCustomer
              ? "The selected entity has no Operations Team assigned"
              : "Operations Team is required",
          );
        }
      }

      return { valid: errors.length === 0, errors };
    },
  }));

  // MUI fires onInputChange with reason "reset" whenever it syncs the input to a
  // selected value — not just when the user types. Treating those as searches
  // sent the selected option's label back as a query and emptied the list.
  const handleClientSearchChange = (_event, newInputValue, reason) => {
    if (reason !== "input") return;
    const inputValue = (newInputValue || "").trim();
    setClientSearchInput(inputValue);
    debouncedClientSearch(inputValue);
  };
  const handleProjectSearchChange = (_event, newInputValue, reason) => {
    if (reason !== "input") return;
    const inputValue = (newInputValue || "").trim();
    setProjectSearchInput(inputValue);
    debouncedProjectSearch(inputValue);
  };

  const debouncedClientSearch = useRef(
    debounce((query) => {
      setClientQueryParams(query);
    }, 500),
  ).current;

  const debouncedProjectSearch = useRef(
    debounce((query) => {
      setProjectQueryParams(query);
    }, 500),
  ).current;

  const formattedClientData = useMemo(
    () =>
      clientsData?.data?.data?.map((client) => ({
        label: client.clientName,
        value: client.id,
        team: client.team
          ? {
              label: client.team.name,
              value: client.team.id,
            }
          : null,
        secondaryTeam: client.secondaryTeam
          ? {
              label: client.secondaryTeam.name,
              value: client.secondaryTeam.id,
            }
          : null,
      })) || [],
    [clientsData],
  );

  // Derived, not stored in state — the old useEffect+setState left the dropdown
  // one render behind, so a freshly picked client showed "No projects" until
  // the user closed and reopened it.
  const formattedProjectData = useMemo(() => {
    const projects =
      projectsData?.data?.projects?.map((project) => ({
        label: project.title,
        value: project.id,
        clientId: project.client?.id,
      })) || [];

    // No client yet: show everything so a project can be picked first.
    if (!overview?.clientId) return projects;

    // Belt and braces — the request is already scoped by clientId.
    return projects.filter((project) => project.clientId === overview.clientId);
  }, [projectsData, overview?.clientId]);
  // Service Delivery team options (primary team of the selected client).
  const teamOptions = useMemo(() => {
    if (overview?.clientId) {
      const selectedClient = formattedClientData.find((c) => c.value === overview.clientId);

      if (selectedClient?.team) {
        return [selectedClient.team];
      }
    }
    if (props.isEditMode && props.workOrderData?.team) {
      return [
        {
          label: props.workOrderData.team.name,
          value: props.workOrderData.team.id,
        },
      ];
    }

    return [];
  }, [overview?.clientId, formattedClientData, props.isEditMode, props.workOrderData]);

  // Operations team options (secondary team of the selected client).
  const secondaryTeamOptions = useMemo(() => {
    if (overview?.clientId) {
      const selectedClient = formattedClientData.find((c) => c.value === overview.clientId);

      if (selectedClient?.secondaryTeam) {
        return [selectedClient.secondaryTeam];
      }
    }
    if (props.isEditMode && props.workOrderData?.secondaryTeam) {
      return [
        {
          label: props.workOrderData.secondaryTeam.name,
          value: props.workOrderData.secondaryTeam.id,
        },
      ];
    }

    return [];
  }, [overview?.clientId, formattedClientData, props.isEditMode, props.workOrderData]);

  // Both team fields are read-only mirrors of the selected client. Say so in the
  // placeholder rather than leaving a greyed-out "No teams" that reads as broken.
  const buildTeamPlaceholder = (options) => {
    if (!overview?.clientId) return "Select a client first";
    if (!options.length) return "This client has no team linked";
    return "";
  };

  const teamPlaceholder = buildTeamPlaceholder(teamOptions);
  const secondaryTeamPlaceholder = buildTeamPlaceholder(secondaryTeamOptions);

  // Both teams are required but can only be set on the client, so an unlinked
  // client is otherwise a dead end. Opens in a new tab to protect the
  // in-progress work order; react-query refetches clients on window focus, so
  // the team appears here as soon as the user comes back.
  const renderLinkTeamPrompt = (hasNoTeam) =>
    hasNoTeam ? (
      <a
        href={`/workBoard/clients/edit/${overview.clientId}`}
        target="_blank"
        rel="noreferrer"
        // Sits inside FieldLabel's 26px row, so it can't grow the label and
        // knock this field out of line with the one beside it.
        className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-primary/30 bg-primary/5 px-3 py-1 text-xs font-normal leading-none text-primary no-underline transition-colors hover:border-primary/60 hover:bg-primary/10"
      >
        Link a team
        <i className="fa-solid fa-arrow-up-right-from-square text-[10px]" />
      </a>
    ) : null;

  const clientHasNoTeam = !!overview?.clientId && !teamOptions.length;
  const clientHasNoSecondaryTeam = !!overview?.clientId && !secondaryTeamOptions.length;

  const slaOptions = [
    {
      label: "2H",
      value: "2H",
    },
    {
      label: "4H",
      value: "4H",
    },
    {
      label: "SBD",
      value: "SBD",
    },
    {
      label: "NBD",
      value: "NBD",
    },
    {
      label: "2BD",
      value: "2BD",
    },
    {
      label: "Planned",
      value: "Planned",
    },
  ];

  useEffect(() => {
    if (props.isEditMode && props.workOrderData) {
      const data = props.workOrderData;
      const hydratedOverview = {
        title: data.title || "",
        serviceLevelAgreeement: data.serviceLevelAgreeement || "",
        incidentId: data.incidentId || "",
        clientId: data.client?.id || null,
        projectId: data.project?.id || null,
        teamId: data.team?.id || null,
        secondaryTeamId: data.secondaryTeam?.id || null,
        templateId: data.template?.id || null,
        isExportDisable: data.isExportDisable || false,
        isCounterOffersDisable: data.isCounterOffersDisable || false,
        isGpsRequire: data.isGpsRequire || false,
      };

      dispatch(
        updateWorkOrderForm({
          step: "summary",
          data: { overview: hydratedOverview },
        }),
      );
      // Baseline for the edit modal's Save Changes button — see useSectionChanges.
      props.onHydrate?.(hydratedOverview);
    }
  }, [props.isEditMode, props.workOrderData, dispatch]);

  const handleChange = (event) => {
    const { name, value, checked, type } = event.target;

    const updatedOverview = {
      [name]: type === "checkbox" ? checked : value,
    };

    if (name === "projectId") {
      if (!value) {
        updatedOverview.projectId = null;
      }
      dispatch(
        updateWorkOrderFormValidation({
          fieldName: "projectId",
          data: false,
        }),
      );
      const selectedProject = formattedProjectData?.find((proj) => proj.value === value);
      if (selectedProject?.clientId) {
        const selectedClient = formattedClientData.find(
          (client) => client.value === selectedProject.clientId,
        );

        updatedOverview.clientId = selectedProject.clientId;
        updatedOverview.teamId = selectedClient?.team?.value || null;
        updatedOverview.teamName = selectedClient?.team?.label || null;
        updatedOverview.secondaryTeamId = selectedClient?.secondaryTeam?.value || null;
        updatedOverview.secondaryTeamName = selectedClient?.secondaryTeam?.label || null;

        // The project back-fills the client and both teams, so clear their errors
        // here too — otherwise "Client is required" lingers under a filled field.
        dispatch(
          updateWorkOrderFormValidation({
            fieldName: "clientId",
            data: false,
          }),
        );
        dispatch(
          updateWorkOrderFormValidation({
            fieldName: "teamId",
            data: updatedOverview.teamId ? false : null,
          }),
        );
        dispatch(
          updateWorkOrderFormValidation({
            fieldName: "secondaryTeamId",
            data: updatedOverview.secondaryTeamId ? false : null,
          }),
        );
      }
    }

    if (name === "teamId") {
      const selectedTeam = teamOptions.find((team) => team.value === value);
      updatedOverview.teamId = selectedTeam?.value || null;
      updatedOverview.teamName = selectedTeam?.label || null;
    }

    if (name === "secondaryTeamId") {
      const selectedTeam = secondaryTeamOptions.find((team) => team.value === value);
      updatedOverview.secondaryTeamId = selectedTeam?.value || null;
      updatedOverview.secondaryTeamName = selectedTeam?.label || null;
    }

    if (name === "clientId") {
      updatedOverview.projectId = null;
      const selectedClient = formattedClientData.find((client) => client.value === value);
      updatedOverview.teamId = selectedClient?.team?.value || null;
      updatedOverview.teamName = selectedClient?.team?.label || null;
      updatedOverview.secondaryTeamId = selectedClient?.secondaryTeam?.value || null;
      updatedOverview.secondaryTeamName = selectedClient?.secondaryTeam?.label || null;
      dispatch(
        updateWorkOrderFormValidation({
          fieldName: "projectId",
          data: null,
        }),
      );
      // Clear team errors once (and only if) the client supplies each team.
      dispatch(
        updateWorkOrderFormValidation({
          fieldName: "teamId",
          data: updatedOverview.teamId ? false : null,
        }),
      );
      dispatch(
        updateWorkOrderFormValidation({
          fieldName: "secondaryTeamId",
          data: updatedOverview.secondaryTeamId ? false : null,
        }),
      );
      if (!value) {
        updatedOverview.clientId = null;
        dispatch(
          updateWorkOrderFormValidation({
            fieldName: "clientId",
            data: null,
          }),
        );
      } else {
        dispatch(
          updateWorkOrderFormValidation({
            fieldName: "clientId",
            data: false,
          }),
        );
      }
    }

    dispatch(
      updateWorkOrderForm({
        step: "summary",
        data: {
          overview: updatedOverview,
        },
      }),
    );

    if (name === "title") {
      dispatch(
        updateWorkOrderFormValidation({
          fieldName: "title",
          data: !value,
        }),
      );
    }
  };

  // SLA is customer-facing but sits in a different slot per role: customers see it
  // beside the Entity field, clients see it below the two auto-filled team fields.
  const slaField = (
    <BaseAutoSelect
      placeholder="Select SLA"
      fullHeight={false}
      fullWidth
      labelShrink={false}
      id="serviceLevelAgreeement"
      name="serviceLevelAgreeement"
      label="SLA"
      options={slaOptions}
      required={false}
      value={overview?.serviceLevelAgreeement || null}
      onChange={handleChange}
    />
  );

  return (
    <BaseCard className="py-2 sm:py-4 lg:px-16 px-5 mt-1 rounded-lg" elevation={1}>
      <div className="border-bottom mb-2 sm:mb-4">
        <h6 className="mb-1 sm:mb-2 text-dark">Overview</h6>
      </div>
      <form>
        <div className="grid grid-cols-2 gap-y-4 sm:gap-y-5 items-start">
          <div className="lg:mr-10 lg:col-span-1 col-span-2">
            <BaseTextField
              error={workOrderFormValidation?.title}
              errorMsg="Title is required"
              fullWidth
              fullHeight={false}
              id="title"
              name="title"
              label="Work Order Title"
              hint="(A clear title helps providers understand your project)"
              requirement="required"
              labelShrink={false}
              className="col-span-4"
              onChange={handleChange}
              value={overview?.title}
              /*  onBlur={() => {
                dispatch(
                  updateWorkOrderFormValidation({
                    fieldName: "title",
                    data: false,
                  })
                );
              }} */
              isDefaultField={false}
              inputProps={{ maxLength: TITLE_MAX_LENGTH }}
              labelRightMsg={`${overview?.title?.length || 0}/${TITLE_MAX_LENGTH}`}
            />
          </div>
          <div className="lg:col-span-1 col-span-2">
            <BaseTextField
              fullHeight={false}
              fullWidth
              labelShrink={false}
              id="incidentId"
              name="incidentId"
              label="Incident ID"
              className="col-span-4"
              requirement="optional"
              value={overview?.incidentId}
              onChange={handleChange}
              isDefaultField={false}
            />
          </div>

          <div className="lg:mr-10 lg:col-span-1 col-span-2">
            <BaseAutoSelect
              isDefaultField={false}
              disabled={isClientLocked}
              fullHeight={false}
              fullWidth
              placeholder={isCustomer ? "No entities" : "No clients"}
              label={isCustomer ? "Entity" : "Client"}
              name="clientId"
              labelShrink={false}
              options={formattedClientData}
              requirement="required"
              loading={isClientsLoading}
              loadingText="Loading clients…"
              noOptionsText={
                clientQueryParams ? "No clients match your search" : "No clients available"
              }
              value={overview?.clientId || null}
              onChange={handleChange}
              onInputChange={handleClientSearchChange}
              onOpen={() => {
                setClientSearchInput("");
                setClientQueryParams("");
              }}
              error={workOrderFormValidation?.clientId}
              errorMsg={isCustomer ? "Entity is required" : "Client is required"}
            />
          </div>

          {/* Customers only get Entity + SLA, so SLA fills the column beside it. */}
          {isCustomer && <div className="lg:col-span-1 col-span-2">{slaField}</div>}

          {!isCustomer && (
            <>
              {/* Project pairs with Client on the same row. */}
              <div className="lg:col-span-1 col-span-2">
                <BaseAutoSelect
                  placeholder="Search or select a project"
                  fullHeight={false}
                  fullWidth
                  labelShrink={false}
                  id="projectId"
                  name="projectId"
                  label="Project"
                  options={formattedProjectData}
                  requirement="required"
                  loading={isProjectsLoading}
                  loadingText="Loading projects…"
                  noOptionsText={
                    overview?.clientId
                      ? "No projects for this client"
                      : projectQueryParams
                        ? "No projects match your search"
                        : "No projects available"
                  }
                  value={overview?.projectId || null}
                  onChange={handleChange}
                  onInputChange={handleProjectSearchChange}
                  onOpen={() => {
                    setProjectSearchInput("");
                    setProjectQueryParams("");
                  }}
                  error={workOrderFormValidation?.projectId}
                  errorMsg="Project is required"
                />
              </div>

              <div className="lg:mr-10 lg:col-span-1 col-span-2">
                <BaseAutoSelect
                  disabled
                  placeholder={teamPlaceholder}
                  hint="(Set automatically from the client)"
                  fullHeight={false}
                  fullWidth
                  labelShrink={false}
                  id="teamId"
                  name="teamId"
                  label="Service Delivery Team"
                  options={teamOptions}
                  requirement="required"
                  endContent={renderLinkTeamPrompt(clientHasNoTeam)}
                  value={overview?.teamId || null}
                  onChange={handleChange}
                  error={workOrderFormValidation?.teamId}
                  errorMsg="Service Delivery Team is required"
                />
              </div>
              {/* Right-hand column: no right margin, so the two auto-filled team
                  fields sit side by side on their own row. */}
              <div className="lg:col-span-1 col-span-2">
                <BaseAutoSelect
                  disabled
                  placeholder={secondaryTeamPlaceholder}
                  hint="(Set automatically from the client)"
                  fullHeight={false}
                  fullWidth
                  labelShrink={false}
                  id="secondaryTeamId"
                  name="secondaryTeamId"
                  label="Operations Team"
                  options={secondaryTeamOptions}
                  requirement="required"
                  endContent={renderLinkTeamPrompt(clientHasNoSecondaryTeam)}
                  value={overview?.secondaryTeamId || null}
                  onChange={handleChange}
                  error={workOrderFormValidation?.secondaryTeamId}
                  errorMsg="Operations Team is required"
                />
              </div>

              <div className="lg:mr-10 lg:col-span-1 col-span-2">{slaField}</div>

              {/* <BaseAutoSelect
              fullHeight={false}
              fullWidth
              labelShrink={false}
              id="templateId"
              name="templateId"
              label="Template"
              className="col-span-4"
              placeholder="No templates"
              value={overview?.template || []}
              onChange={handleChange}
              options={[]}
              isDefaultField={false}
            /> */}

              <div className="col-span-2 flex items-center">
                <div className="grid grid-cols-9 items-center">
                  <span className="text-sm font-normal xl:col-span-2 col-span-12">
                    Work order options
                  </span>
                  <div className="xl:ml-4 xl:col-span-2 col-span-12">
                    <BaseCheckbox
                      label="Disable export button"
                      name="isExportDisable"
                      checked={overview?.isExportDisable}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="xl:ml-4 xl:col-span-2 col-span-12">
                    <BaseCheckbox
                      label="Disable counter offers"
                      name="isCounterOffersDisable"
                      checked={overview?.isCounterOffersDisable}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="xl:ml-4 xl:col-span-3 col-span-12">
                    <BaseCheckbox
                      label="Require GPS during check in and check out"
                      name="isGpsRequire"
                      checked={overview?.isGpsRequire}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </form>
    </BaseCard>
  );
});

export default OverviewForm;
